import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { WORKFLOW_SEQUENCE, PROJECTS, UPSTREAM_DEPENDENCIES, SPECIALIST_LANES, canStartProject } from "../config/wiseboardConfig";
import type { ProjectStatus } from "../config/wiseboardConfig";
import { CheckCircle, AlertTriangle, Clock, ShieldAlert, BarChart3 } from "lucide-react";

interface Props {
  caseId: string;
  refreshKey?: number;
}

export default function CaseReadinessPanel({ caseId, refreshKey }: Props) {
  const [projects, setProjects] = useState<{ project_type: string; status: string }[]>([]);
  const [evidence, setEvidence] = useState<{ status: string; hypothesis_id: string }[]>([]);
  const [roster, setRoster] = useState<{ lane: string; selected: boolean }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!caseId) return;
    const load = async () => {
      const [projRes, evRes, rosterRes] = await Promise.all([
        supabase.from("wiseboard_projects").select("project_type, status").eq("case_id", caseId),
        supabase.from("wiseboard_evidence_matrix").select("status, hypothesis_id").eq("case_id", caseId),
        supabase.from("wiseboard_specialist_roster").select("lane, selected").eq("case_id", caseId),
      ]);
      setProjects(projRes.data || []);
      setEvidence(evRes.data || []);
      setRoster(rosterRes.data || []);
      setLoading(false);
    };
    load();
  }, [caseId, refreshKey]);

  if (loading) return null;

  const statusMap: Record<string, ProjectStatus> = {};
  projects.forEach(p => { statusMap[p.project_type] = p.status as ProjectStatus; });
  const selectedLanes = roster.filter(r => r.selected).map(r => r.lane);

  // Active projects (non-specialist excluded lanes filtered out)
  const activeProjects = WORKFLOW_SEQUENCE.filter(pid => {
    if (SPECIALIST_LANES.includes(pid) && !selectedLanes.includes(pid)) return false;
    return true;
  });

  const total = activeProjects.length;
  const approved = activeProjects.filter(p => statusMap[p] === "approved").length;
  const inProgress = activeProjects.filter(p => statusMap[p] === "in_progress").length;
  const onHold = activeProjects.filter(p => statusMap[p] === "hold").length;
  const approvalPct = total > 0 ? Math.round((approved / total) * 100) : 0;

  // Evidence sufficiency
  const evTotal = evidence.length;
  const evFound = evidence.filter(e => e.status === "DATA_FOUND").length;
  const evNotFound = evidence.filter(e => e.status === "NOT_FOUND").length;
  const evAssumptions = evidence.filter(e => e.status === "ASSUMPTION").length;
  const evStale = evidence.filter(e => e.status === "POTENTIALLY_STALE").length;
  const evSufficiencyPct = evTotal > 0 ? Math.round((evFound / evTotal) * 100) : 0;

  // Blocking issues
  const blockers: string[] = [];
  for (const pid of activeProjects) {
    const status = statusMap[pid] || "not_started";
    if (status === "not_started" || status === "in_progress") {
      const { canStart, blockers: projBlockers } = canStartProject(pid, statusMap, selectedLanes);
      if (!canStart && status === "not_started") {
        // Only show blockers for projects that should be next
        const deps = UPSTREAM_DEPENDENCIES[pid] || [];
        const allDepsApproved = deps.every(d => statusMap[d] === "approved");
        if (deps.length > 0 && !allDepsApproved) continue; // not yet relevant
        projBlockers.forEach(b => blockers.push(`${PROJECTS[pid]?.name || pid}: ${b}`));
      }
    }
    if (status === "hold") {
      blockers.push(`${PROJECTS[pid]?.name || pid} is on hold`);
    }
  }

  // Next action
  const nextActions: string[] = [];
  for (const pid of activeProjects) {
    const status = statusMap[pid] || "not_started";
    if (status === "in_progress") {
      nextActions.push(`Complete & approve ${PROJECTS[pid]?.name || pid}`);
    }
  }
  if (nextActions.length === 0) {
    const nextToStart = activeProjects.find(pid => {
      const s = statusMap[pid] || "not_started";
      if (s !== "not_started") return false;
      const { canStart } = canStartProject(pid, statusMap, selectedLanes);
      return canStart;
    });
    if (nextToStart) nextActions.push(`Start ${PROJECTS[nextToStart]?.name || nextToStart}`);
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          Case Readiness
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress bars */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Projects Approved</span>
              <span className="font-medium">{approved}/{total} ({approvalPct}%)</span>
            </div>
            <Progress value={approvalPct} className="h-2" />
            <div className="flex gap-2 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-500" />{approved} approved</span>
              <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-blue-500" />{inProgress} in progress</span>
              {onHold > 0 && <span className="flex items-center gap-1"><ShieldAlert className="h-3 w-3 text-red-500" />{onHold} on hold</span>}
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Evidence Sufficiency</span>
              <span className="font-medium">{evFound}/{evTotal} ({evSufficiencyPct}%)</span>
            </div>
            <Progress value={evSufficiencyPct} className="h-2" />
            <div className="flex gap-2 text-[10px] text-muted-foreground flex-wrap">
              <span>{evFound} found</span>
              <span>{evNotFound} missing</span>
              <span>{evStale} stale</span>
              <span>{evAssumptions} assumptions</span>
            </div>
          </div>
        </div>

        {/* Blockers & Next Actions */}
        <div className="grid md:grid-cols-2 gap-4">
          {blockers.length > 0 && (
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs font-medium text-destructive">
                <AlertTriangle className="h-3.5 w-3.5" />
                Blocking Issues ({blockers.length})
              </div>
              <ul className="text-[10px] text-muted-foreground space-y-0.5 ml-5 list-disc">
                {blockers.slice(0, 5).map((b, i) => <li key={i}>{b}</li>)}
                {blockers.length > 5 && <li>+{blockers.length - 5} more</li>}
              </ul>
            </div>
          )}

          {nextActions.length > 0 && (
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs font-medium text-primary">
                <CheckCircle className="h-3.5 w-3.5" />
                Next Actions
              </div>
              <ul className="text-[10px] text-muted-foreground space-y-0.5 ml-5 list-disc">
                {nextActions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
